import React from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import type { ChangeCatalogItem, ChangeDateRevision, ChangeType } from '../../../types/changes';
import { isEmergencyChangeType, TYPE_LABELS } from '../changeLabels';
import {
  CHANGE_FIELD_LIMITS,
  needsGoLiveOverride,
  patchGoLiveStart,
  type ChangeFormValues,
  type GoLiveOverrideState,
} from '../changeValidation';
import { requiresThursdayGoLive, toLocalDateInput } from '../goLiveWindow';
import { nativeDateRevisionOf } from '../changeDateRevisions';
import CatalogSelect from './CatalogSelect';
import ChangeDateRevisionHistory from './ChangeDateRevisionHistory';
import ChangeSapRequestFields from './ChangeSapRequestFields';

interface Props {
  /** Prefixo dos ids dos campos — evita id duplicado quando criar e editar coexistem. */
  idPrefix: string;
  form: ChangeFormValues;
  onFormChange: (patch: Partial<ChangeFormValues>) => void;
  categories: ChangeCatalogItem[];
  requesterAreas: ChangeCatalogItem[];
  /** Aceito por compatibilidade; o tipo fica na subtarefa, não neste formulário. */
  taskTypes?: ChangeCatalogItem[];
  override: GoLiveOverrideState;
  onOverrideChange: (patch: Partial<Omit<GoLiveOverrideState, 'isAdmin'>>) => void;
  /** Cadastros atuais da mudança em edição (podem estar desativados). */
  currentCategory?: { id: number; name: string } | null;
  currentRequesterArea?: { id: number; name: string } | null;
  /** Campos exclusivos da criação (dono e solicitante), renderizados no topo. */
  children?: React.ReactNode;
  dateRevisions?: Record<string, ChangeDateRevision>;
}

/**
 * Campos compartilhados pelos formulários de criação e edição de mudança.
 *
 * As duas telas mantinham o mesmo bloco de ~150 linhas de JSX duplicado, então
 * um campo novo (ou um `maxLength`) só entrava em uma delas.
 */
const ChangeFormFields: React.FC<Props> = ({
  idPrefix,
  form,
  onFormChange,
  categories,
  requesterAreas,
  override,
  onOverrideChange,
  currentCategory,
  currentRequesterArea,
  children,
  dateRevisions,
}) => {
  const emergency = isEmergencyChangeType(form.change_type);
  const needsThursday = requiresThursdayGoLive(form.change_type);
  const outsideWindow = needsGoLiveOverride(form);
  const endBeforeStart =
    Boolean(form.go_live_start_at) &&
    Boolean(form.go_live_end_at) &&
    new Date(form.go_live_end_at) < new Date(form.go_live_start_at);

  return (
    <Row className="g-3">
      {children}

      <Col xs={12}>
        <Form.Label>Título</Form.Label>
        <Form.Control
          value={form.title}
          maxLength={CHANGE_FIELD_LIMITS.title}
          onChange={(e) => onFormChange({ title: e.target.value })}
        />
      </Col>

      <Col xs={12} md={6}>
        <Form.Label>Tipo</Form.Label>
        <Form.Select
          value={form.change_type}
          onChange={(e) => {
            const change_type = e.target.value as ChangeType;
            onFormChange(
              isEmergencyChangeType(change_type)
                ? {
                    change_type,
                    risk_committee_start_at: '',
                    risk_committee_end_at: '',
                    camud_go_no_go_date: '',
                  }
                : { change_type },
            );
          }}
        >
          {(Object.keys(TYPE_LABELS) as ChangeType[]).map((value) => (
            <option key={value} value={value}>
              {TYPE_LABELS[value]}
            </option>
          ))}
        </Form.Select>
      </Col>

      <Col xs={12} md={6}>
        <Form.Label>Projeto</Form.Label>
        <Form.Control
          value={form.project}
          maxLength={CHANGE_FIELD_LIMITS.shortText}
          onChange={(e) => onFormChange({ project: e.target.value })}
        />
      </Col>

      <Col xs={12} md={6}>
        <CatalogSelect
          id={`${idPrefix}-category`}
          label="Categoria"
          items={categories}
          value={form.category_id}
          current={currentCategory}
          onChange={(category_id) => onFormChange({ category_id })}
        />
      </Col>

      <Col xs={12} md={6}>
        <CatalogSelect
          id={`${idPrefix}-requester-area`}
          label="Área solicitante"
          items={requesterAreas}
          value={form.requester_area_id}
          current={currentRequesterArea}
          onChange={(requester_area_id) => onFormChange({ requester_area_id })}
        />
      </Col>

      <ChangeSapRequestFields idPrefix={idPrefix} form={form} onFormChange={onFormChange} />

      <Col xs={12} md={6}>
        <Form.Label htmlFor={`${idPrefix}-go-live-start`}>
          {emergency ? 'Início da implementação' : 'Início do Go Live'}
        </Form.Label>
        <Form.Control
          id={`${idPrefix}-go-live-start`}
          type="datetime-local"
          value={form.go_live_start_at}
          disabled={Boolean(nativeDateRevisionOf(dateRevisions, 'go_live_window')?.locked)}
          onChange={(e) => onFormChange(patchGoLiveStart(form, e.target.value))}
        />
        {needsThursday ? (
          <Form.Text className={outsideWindow ? 'text-danger' : 'text-muted'}>
            Normal/Padrão: Go Live deve começar numa quinta-feira.
          </Form.Text>
        ) : null}
      </Col>

      <Col xs={12} md={6}>
        <Form.Label htmlFor={`${idPrefix}-go-live-end`}>
          {emergency ? 'Fim da implementação' : 'Fim do Go Live'}
        </Form.Label>
        <Form.Control
          id={`${idPrefix}-go-live-end`}
          type="datetime-local"
          value={form.go_live_end_at}
          min={form.go_live_start_at || undefined}
          disabled={Boolean(nativeDateRevisionOf(dateRevisions, 'go_live_window')?.locked)}
          onChange={(e) => onFormChange({ go_live_end_at: e.target.value })}
        />
        <ChangeDateRevisionHistory
          showRemaining
          revision={nativeDateRevisionOf(dateRevisions, 'go_live_window')}
        />
        {endBeforeStart ? (
          <Form.Text className="text-danger">O fim precisa ser maior ou igual ao início.</Form.Text>
        ) : null}
      </Col>

      {!emergency ? (
      <>
      <Col xs={12} md={6}>
        <Form.Label htmlFor={`${idPrefix}-risk-start`}>Riscos e Impactos</Form.Label>
        <Form.Control
          id={`${idPrefix}-risk-start`}
          type="date"
          value={toLocalDateInput(form.risk_committee_start_at)}
          disabled={Boolean(nativeDateRevisionOf(dateRevisions, 'risk_committee_window')?.locked)}
          onChange={(e) => {
            const value = toLocalDateInput(e.target.value);
            onFormChange({ risk_committee_start_at: value, risk_committee_end_at: value });
          }}
        />
        <ChangeDateRevisionHistory
          showRemaining
          revision={nativeDateRevisionOf(dateRevisions, 'risk_committee_window')}
        />
        <Form.Text className="text-muted">Somente data, sem horário. Regular: terça-feira.</Form.Text>
      </Col>

      <Col xs={12} md={6}>
        <Form.Label htmlFor={`${idPrefix}-camud-date`}>Agendamento CAMUD Go/No-Go</Form.Label>
        <Form.Control
          id={`${idPrefix}-camud-date`}
          type="date"
          value={toLocalDateInput(form.camud_go_no_go_date)}
          disabled={Boolean(nativeDateRevisionOf(dateRevisions, 'camud_go_no_go_date')?.locked)}
          onChange={(e) => onFormChange({ camud_go_no_go_date: toLocalDateInput(e.target.value) })}
        />
        <ChangeDateRevisionHistory
          showRemaining
          revision={nativeDateRevisionOf(dateRevisions, 'camud_go_no_go_date')}
        />
        <Form.Text className="text-muted">
          Somente data, sem horário. Regular: quinta-feira.
        </Form.Text>
      </Col>
      </>
      ) : null}

      {outsideWindow && override.isAdmin ? (
        <Col xs={12}>
          <Form.Check
            type="checkbox"
            id={`${idPrefix}-force-go-live`}
            label="Forçar Go Live fora da janela (admin)"
            checked={override.forceOutside}
            onChange={(e) => onOverrideChange({ forceOutside: e.target.checked })}
          />
          {override.forceOutside ? (
            <Form.Control
              className="mt-2"
              placeholder="Motivo do force"
              maxLength={CHANGE_FIELD_LIMITS.longText}
              value={override.forceReason}
              onChange={(e) => onOverrideChange({ forceReason: e.target.value })}
            />
          ) : null}
        </Col>
      ) : null}

      <Col xs={12}>
        <Form.Label>Descrição</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={form.description}
          maxLength={CHANGE_FIELD_LIMITS.longText}
          onChange={(e) => onFormChange({ description: e.target.value })}
        />
      </Col>

      <Col xs={12}>
        <Form.Label>Escopo</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={form.scope}
          maxLength={CHANGE_FIELD_LIMITS.longText}
          onChange={(e) => onFormChange({ scope: e.target.value })}
        />
      </Col>

      <Col xs={12}>
        <Form.Label>Usuários impactados</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={form.impacted_users}
          maxLength={CHANGE_FIELD_LIMITS.longText}
          onChange={(e) => onFormChange({ impacted_users: e.target.value })}
        />
      </Col>

      <Col xs={12}>
        <Form.Label>Plano de rollback</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={form.rollback_plan}
          maxLength={CHANGE_FIELD_LIMITS.longText}
          onChange={(e) => onFormChange({ rollback_plan: e.target.value })}
        />
      </Col>
    </Row>
  );
};

export default ChangeFormFields;
